/**
 * useSEO.js
 *
 * Fetches SEO values from /api/content/seo (your MongoDB via the existing
 * content route) and applies them to the page's <head> tags.
 *
 * Admin changes SEO fields in AdminSite → hits Save → saved to DB
 * → next page load picks up the new values automatically.
 * Zero code push needed.
 *
 * Usage (same as before — no change needed in page files):
 *   import { useSEO, SEO_PAGES } from '../hooks/useSEO';
 *   useSEO(SEO_PAGES.home);
 *
 * The SEO_PAGES presets act as FALLBACKS if the DB has no value yet.
 */

import { useEffect } from 'react';
import api from '../services/api';

/* ─── Fallback presets (used when DB field is empty) ─── */
const SITE_URL  = 'https://herambaayurveda.in';
const SITE_NAME = 'Heramba Ayurveda';

export const SEO_PAGES = {
  home: {
    title:       'Heramba Ayurveda | Authentic Ayurvedic Clinic & Holistic Care',
    description: 'Heramba Ayurveda offers authentic Ayurvedic consultations, Panchakarma therapies, Shirodhara, herbal treatments, and holistic healing rooted in 5,000 years of Vedic wisdom. Book your online consultation today.',
    canonical:   `${SITE_URL}/`,
  },
  about: {
    title:       'About Us | Heramba Ayurveda',
    description: 'Discover the story, mission, and philosophy behind Heramba Ayurveda — a clinic dedicated to authentic Ayurvedic healing and patient-centred holistic wellness.',
    canonical:   `${SITE_URL}/about`,
  },
  services: {
    title:       'Ayurvedic Services | Panchakarma, Shirodhara & More | Heramba Ayurveda',
    description: 'Explore our range of authentic Ayurvedic services: Panchakarma detox, Shirodhara, dietary consultations, yoga therapy, and classical herbal treatments.',
    canonical:   `${SITE_URL}/services`,
  },
  ourDoctors: {
    title:       'Our Ayurvedic Doctors | Heramba Ayurveda',
    description: 'Meet our experienced Ayurvedic physicians. Each doctor brings deep classical training and a patient-first approach to every consultation.',
    canonical:   `${SITE_URL}/our-doctors`,
  },
  request: {
    title:       'Book a Consultation | Heramba Ayurveda',
    description: 'Book an in-person or online Ayurvedic consultation with Heramba Ayurveda. Start your journey to holistic wellness today.',
    canonical:   `${SITE_URL}/request`,
  },
  status: {
    title:       'Consultation Status | Heramba Ayurveda',
    description: 'Track the status of your Ayurvedic consultation request at Heramba Ayurveda.',
    canonical:   `${SITE_URL}/status`,
  },
};

/* ─── Shared DB cache so we only fetch once per session ─── */
let _cache    = null;
let _fetching = null;

function fetchSEOData() {
  if (_cache)    return Promise.resolve(_cache);
  if (_fetching) return _fetching;
  _fetching = api.get('/content/seo')
    .then(res => {
      _cache = res.data?.data || {};
      return _cache;
    })
    .catch(() => {
      _cache = {};
      return _cache;
    })
    .finally(() => { _fetching = null; });
  return _fetching;
}

/* ─── Helper: upsert a <meta> tag ─── */
function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const raw = selector.replace('meta[', '').replace(']', '');
    const eqIdx = raw.indexOf('=');
    const attrName = raw.slice(0, eqIdx).trim();
    const attrVal  = raw.slice(eqIdx + 1).replace(/"/g, '').trim();
    el.setAttribute(attrName, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

/* ─── Helper: upsert a <link> tag ─── */
function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/* ─── Apply all meta tags ─── */
function applyMeta({ title, description, ogTitle, ogDesc, ogImage, canonical }) {
  if (title)       document.title = title;
  if (description) setMeta('meta[name="description"]',        'content', description);
  if (canonical)   setLink('canonical', canonical);

  setMeta('meta[property="og:title"]',       'content', ogTitle || title);
  setMeta('meta[property="og:description"]', 'content', ogDesc  || description);
  setMeta('meta[property="og:url"]',         'content', canonical);
  if (ogImage) setMeta('meta[property="og:image"]', 'content', ogImage);

  setMeta('meta[name="twitter:title"]',       'content', ogTitle || title);
  setMeta('meta[name="twitter:description"]', 'content', ogDesc  || description);
  if (ogImage) setMeta('meta[name="twitter:image"]', 'content', ogImage);
}

/**
 * useSEO — call at the top of every public page component.
 *
 * @param {Object} pagePreset  — one of SEO_PAGES.xxx
 *
 * Flow:
 *  1. Applies the hardcoded preset instantly (zero flicker)
 *  2. Fetches /api/content/seo from DB (cached after first call)
 *  3. Overrides with any non-empty DB values (admin-controlled)
 */
export function useSEO(pagePreset = {}) {
  useEffect(() => {
    /* Step 1 — instant preset apply */
    applyMeta({
      title:       pagePreset.title,
      description: pagePreset.description,
      canonical:   pagePreset.canonical,
      ogTitle:     pagePreset.title,
      ogDesc:      pagePreset.description,
    });

    /* Step 2 — fetch DB values and override */
    fetchSEOData().then(db => {
      applyMeta({
        title:       db.seoTitle       || pagePreset.title,
        description: db.seoDescription || pagePreset.description,
        canonical:   db.seoCanonical   || pagePreset.canonical,
        ogTitle:     db.ogTitle        || db.seoTitle       || pagePreset.title,
        ogDesc:      db.ogDescription  || db.seoDescription || pagePreset.description,
        ogImage:     db.ogImage,
      });

      /* Geo tags */
      if (db.geoRegion)    setMeta('meta[name="geo.region"]',    'content', db.geoRegion);
      if (db.geoPlacename) setMeta('meta[name="geo.placename"]', 'content', db.geoPlacename);

      /* Keywords & author */
      if (db.seoKeywords) setMeta('meta[name="keywords"]', 'content', db.seoKeywords);
      if (db.seoAuthor)   setMeta('meta[name="author"]',   'content', db.seoAuthor);
    });
  }, [pagePreset.canonical]); // re-run when page changes
}